import { useEffect, type FC } from 'react'
import Icon from '@/components/common/Icon'
import StreamingAudioWave from '@/components/common/StreamingAudioWave'
import { useRecorder } from './useRecorder'
import SettingsPopover from './SettingsPopover'
import useSpeechDialogStore from '@/components/SpeechDialog/speech-dialog.store.ts'
import useAudioStore from '@/shared/store/audio.store.tsx'

const RecordPanel: FC = () => {
  const {
    isRecording,
    audioUrl,
    recordingTime,
    countdown,
    handleRecordClick,
    settingsOpen,
    setSettingsOpen,
    frameRef,
    formatTime,
    streamRef,
    handleClose,
  } = useRecorder()
  const { setStopRecorderFn } = useSpeechDialogStore()

  const audioContext = useAudioStore((state) => state.audioContext)

  useEffect(() => {
    setStopRecorderFn(handleClose)
  }, [])

  return (
    <div className="relative flex h-[220px] flex-col gap-3 rounded-lg bg-neutral-900 p-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <span className="flex-1 text-sm font-medium text-white">
          Press the record button and read your script.
        </span>
        <div className="flex items-center" ref={frameRef}>
          <button type="button" onClick={() => setSettingsOpen(true)}>
            <Icon name="frame" />
          </button>
        </div>
        <button type="button" onClick={handleClose}>
          <Icon name="xMark" />
        </button>
      </div>
      {settingsOpen && (
        <SettingsPopover
          anchorRef={frameRef}
          onClose={() => setSettingsOpen(false)}
          open={settingsOpen}
        />
      )}
      <div className="relative flex h-[96px] flex-col items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-white/5 to-white/10">
        {isRecording && streamRef.current && (
          <StreamingAudioWave
            audioContext={audioContext}
            stream={streamRef.current}
            width={440}
            height={48}
          />
        )}
        <div className="absolute top-[1/2] h-px w-full bg-white/10" />
      </div>
      <div className="absolute bottom-5 left-0 right-0 flex w-full flex-col items-center justify-center">
        <button
          type="button"
          className="flex h-[56px] w-[56px] select-none items-center justify-center rounded-full border-[6px] border-neutral-900 bg-red-600 text-2xl font-bold shadow-lg transition-colors duration-200"
          onClick={handleRecordClick}
          disabled={countdown !== undefined}
        >
          {countdown !== undefined ? (
            <span className="text-base font-normal text-white">
              {countdown}
            </span>
          ) : (
            <Icon
              name={isRecording ? 'stop' : 'speech'}
              fill="#fff"
              width={20}
              height={20}
            />
          )}
        </button>
        <span className="text-sm text-white">{formatTime(recordingTime)}</span>
        {audioUrl && <audio controls src={audioUrl} className="mt-2" />}
      </div>
    </div>
  )
}

export default RecordPanel
